// Calendar event types — the ONE list both event pickers render.
//
// Feedback #826 / #827 (Hestia): the Event Type / Category dropdown was not in
// alphabetical order, and "Off Calendar / Vacated" could not be found at all.
// Before this module the Calendar page and the Matter page each carried their
// own hand-typed <option> list; the two had already drifted (the matter page
// offered 149 of the calendar's 171 types, and in a different grouping).
//
// Rules this module enforces, and eventTypes.test.js asserts:
//   • groups are sorted alphabetically by label, and options alphabetically
//     by label within each group (case-insensitive) — the sort is applied at
//     load time below, so the literal can be edited in any order;
//   • every value is unique across the whole list;
//   • no value that either old picker offered is ever dropped.  event_type is
//     stored data (VARCHAR, no CHECK) — removing an option would make an
//     existing event un-re-selectable in its own edit form.  To retire a
//     type, relabel it "(legacy code)" instead.
//
// Group labels carry no emoji in the sort key; the icon is rendered in front.

const RAW_EVENT_TYPE_GROUPS = [
  {
    label: "Client Work", icon: "📁",
    options: [
      { value: "client_work_general", label: "Client Work - General" },
      { value: "client_work_special", label: "Client Work - Special" },
      { value: "follow_up", label: "Follow Up" },
      { value: "draft", label: "Draft" },
      { value: "document_review", label: "Document Review" },
      { value: "inspection", label: "Inspection" },
      { value: "minor_compensation", label: "Minor's Compensation (legacy code)" },
      { value: "court", label: "Court" },
    ],
  },
  {
    label: "Court Events", icon: "⚖️",
    options: [
      { value: "hearing", label: "Hearing - Court" },
      { value: "trial", label: "Trial" },
      { value: "trial_date", label: "Trial Date" },
      { value: "motion_hearing", label: "Motion Hearing" },
      { value: "status_conference", label: "Status Conference" },
      { value: "pretrial_conference", label: "Pretrial Conference" },
      { value: "final_status_conference", label: "Final Status Conference" },
      { value: "settlement_conference", label: "Settlement Conference" },
      { value: "complete_settlement_conference", label: "Complete Settlement Conference" },
      { value: "arbitration", label: "Arbitration" },
      { value: "mediation", label: "Mediation" },
      { value: "mediation_settlement_conference", label: "Mediation/Settlement Conference" },
      { value: "mandatory", label: "MANDATORY" },
      // Feedback #827 — Amicus imported this category as event_type 'reminder',
      // so it existed on 400 events and could never be picked.
      { value: "off_calendar_vacated", label: "Off Calendar / Vacated" },
      { value: "default", label: "Default" },
      { value: "default_hearing", label: "Default Hearing" },
      { value: "class_certification_motion", label: "Class Certification Motion" },
      { value: "law_motion_cutoff", label: "Law and Motion Cut Off" },
      { value: "motions_in_limine", label: "Motions in Limine" },
      { value: "mil_opposition_due", label: "MIL Opposition Due" },
      { value: "mil_reply_due", label: "MIL Reply Due" },
      { value: "mil_response_due", label: "MIL Response Due" },
      { value: "ene_statements", label: "ENE Statements" },
      { value: "closing", label: "Closing" },
      { value: "judgment_due", label: "Judgment Due" },
      { value: "order_due", label: "Order Due" },
      { value: "writ_due", label: "Writ Due" },
    ],
  },
  {
    label: "Depositions & Discovery", icon: "📋",
    options: [
      { value: "deposition", label: "Deposition" },
      { value: "deposition_defense", label: "Deposition - Defense" },
      { value: "deposition_plaintiff", label: "Deposition - Plaintiff" },
      { value: "deposition_expert", label: "Deposition - Expert" },
      { value: "deposition_witness", label: "Deposition - Witness" },
      { value: "witness_interview", label: "Witness Interview" },
      { value: "medical_exam", label: "Medical Exam / IME" },
      { value: "defense_medical_exam", label: "Defense Medical Exam" },
      { value: "meet_confer", label: "Meet and Confer" },
      { value: "inspection_site", label: "Site Inspection" },
      { value: "records_subpoena", label: "Records Subpoena" },
      { value: "subpoena_response_due", label: "Subpoena Response Due" },
    ],
  },
  {
    label: "Filing Deadlines", icon: "⏰",
    options: [
      { value: "report_due", label: "Report Due" },
      { value: "statement_due", label: "Statement Due" },
      { value: "status_reports", label: "Status Reports" },
      { value: "trc_report_due", label: "TRC Report Due" },
      { value: "witness_lists_due", label: "Witness Lists Due" },
      { value: "mediation_questionnaire_due", label: "Mediation Questionnaire Due" },
      { value: "proof_of_service", label: "Proof of Service" },
      { value: "f_proof_of_service", label: "Proof of Service (filed)" },
      { value: "f_ex_parte_application", label: "Ex Parte Application (filed)" },
      { value: "notice", label: "Notice" },
      { value: "petition_juvenile_court", label: "Petition — Juvenile Court" },
      { value: "tort_claim", label: "Tort Claim" },
      { value: "minor_s_compensation", label: "Minor’s Compensation" },
      { value: "transcript", label: "Transcript" },
      { value: "filing_deadline", label: "Filing Deadline" },
      { value: "f_complaint", label: "F-Complaint" },
      { value: "f_amended_complaint", label: "F-Amended Complaint" },
      { value: "f_cross_complaint", label: "F-Cross Complaint" },
      { value: "f_answer", label: "F-Answer" },
      { value: "f_demurrer", label: "F-Demurrer" },
      { value: "f_motion", label: "F-Motion" },
      { value: "f_motion_compel", label: "F-Motion to Compel" },
      { value: "f_motion_compel_further", label: "F-Motion to Compel Further" },
      { value: "f_motion_strike", label: "F-Motion to Strike" },
      { value: "f_motion_summary_adjudication", label: "F-Motion for Summary Adjudication" },
      { value: "f_ex_parte", label: "F-Ex Parte Application" },
      { value: "f_fee_motion", label: "F-Fee Motion" },
      { value: "f_notice", label: "F-Notice" },
      { value: "f_proof_service", label: "F-Proof of Service" },
      { value: "f_summary_judgment", label: "F-Summary Judgment" },
      { value: "f_opposition", label: "F-Opposition" },
      { value: "f_reply", label: "F-Reply" },
      { value: "f_joinder", label: "F-Joinder" },
      { value: "f_stipulation", label: "F-Stipulation" },
      { value: "f_proposed_order", label: "F-Proposed Order" },
    ],
  },
  {
    label: "Brief & Pleading Deadlines", icon: "⏰",
    options: [
      { value: "oppo_smj", label: "Opposition to MSJ" },
      { value: "reply_smj", label: "Reply re MSJ" },
      { value: "opening_briefs_due", label: "Opening Briefs Due" },
      { value: "responding_briefs_due", label: "Responding Briefs Due" },
      { value: "settlement_briefs_due", label: "Settlement Briefs Due" },
      { value: "respond_due", label: "Respond Due" },
      { value: "stipulation", label: "Stipulation" },
      { value: "brief_due", label: "Brief Due" },
      { value: "opening_brief_due", label: "Opening Brief Due" },
      { value: "answering_brief_due", label: "Answering Brief Due" },
      { value: "reply_brief_due", label: "Reply Brief Due" },
      { value: "answering_complaint_due", label: "Answering Complaint Due" },
      { value: "amended_pleadings", label: "Amended Pleadings" },
      { value: "response_to_pleading", label: "Response to Pleading" },
      { value: "opposition_due", label: "Opposition Due" },
      { value: "reply_due", label: "Reply Due" },
      { value: "mediation_brief", label: "Mediation Brief" },
      { value: "mediation_questionnaire", label: "Mediation Questionnaire Due" },
      { value: "declaration_due", label: "Declaration Due" },
      { value: "cmc_statement_due", label: "CMC Statement Due" },
      { value: "joint_report_due", label: "Joint Report Due" },
      { value: "trial_brief_due", label: "Trial Brief Due" },
      { value: "jury_instructions_due", label: "Jury Instructions Due" },
      { value: "verdict_form_due", label: "Verdict Form Due" },
      { value: "witness_list_due", label: "Witness List Due" },
      { value: "exhibit_list_due", label: "Exhibit List Due" },
      { value: "pretrial_statement_due", label: "Pretrial Statement Due" },
      // Amicus "Trial Prep" / "Trial Filings" categories — deadlines leading
      // up to trial (JI / exhibit exchanges, pretrial filings), NOT the trial
      // itself.  The importer used to merge both into 'trial'; re-typed by
      // api/src/config/migrations_amicus_category_retype.sql.
      { value: "trial_prep", label: "Trial Prep" },
      { value: "trial_filings", label: "Trial Filings" },
    ],
  },
  {
    label: "Discovery Deadlines", icon: "⏰",
    options: [
      { value: "produce_records_documents", label: "Produce Records / Documents" },
      { value: "respond_to_meet_and_confer", label: "Respond to Meet and Confer" },
      { value: "discovery_cut_off", label: "Discovery Cut-Off" },
      { value: "discovery_response_due", label: "Discovery Response Due" },
      { value: "expert_discovery_cut_off", label: "Expert Discovery Cut-Off" },
      { value: "written_discovery_cut_off", label: "Written Discovery Cut-Off" },
      { value: "complete_general_fact_discovery", label: "Complete General Fact Discovery" },
      { value: "rebuttal_experts", label: "Rebuttal Experts" },
      { value: "supplemental_due", label: "Supplemental Due" },
      { value: "objection_due", label: "Objection Due" },
      { value: "medical_exam_ime", label: "Medical Exam / IME" },
      { value: "subpoena", label: "Subpoena" },
      { value: "demand", label: "Demand" },
      { value: "discovery_deadline", label: "Discovery Deadline" },
      { value: "discovery_responses_due", label: "Discovery Responses Due" },
      { value: "rogs_responses_due", label: "Interrogatory Responses Due" },
      { value: "rpd_responses_due", label: "Request for Production Responses Due" },
      { value: "rfa_responses_due", label: "Request for Admissions Responses Due" },
      { value: "disclosure_due", label: "Disclosure Due" },
      { value: "initial_disclosures", label: "Initial Disclosures" },
      { value: "supplemental_disclosures", label: "Supplemental Disclosures Due" },
      { value: "discovery_cutoff", label: "Discovery Cutoff (legacy code)" },
      { value: "complete_fact_discovery", label: "Complete General Fact Discovery (legacy code)" },
      { value: "non_expert_discovery_cutoff", label: "Non-Expert Discovery Cut-Off" },
      { value: "expert_designation", label: "Expert Designation" },
      { value: "rebuttal_expert_designation", label: "Rebuttal Expert Designation" },
      { value: "expert_discovery_cutoff", label: "Expert Discovery Cut-Off (legacy code)" },
      { value: "expert_report_due", label: "Expert Report Due" },
    ],
  },
  {
    label: "Other Deadlines", icon: "⏰",
    options: [
      { value: "response_due", label: "Response Due" },
      { value: "appeal_deadline", label: "Appeal Deadline" },
      { value: "notice_of_appeal_due", label: "Notice of Appeal Due" },
      { value: "statute_of_limitations", label: "Statute of Limitations" },
      { value: "sol_tolling_expires", label: "SOL Tolling Expires" },
      { value: "demand_response_due", label: "Demand Response Due" },
      { value: "deadline", label: "General Deadline" },
      { value: "reminder", label: "Reminder" },
    ],
  },
  {
    label: "Meetings", icon: "👥",
    options: [
      { value: "client_meeting", label: "Client Meeting" },
      { value: "initial_consultation", label: "Initial Consultation" },
      // Feedback #846 (Hestia): was "General Meeting", so it sat under G in
      // the A–Z picker and "Meeting" could not be found.  Now matches the
      // Amicus category name ("Meeting" → 'meeting').
      { value: "meeting", label: "Meeting" },
      // Feedback #846: moved here from the Personal group — a phone call is
      // a call, like a conference or Zoom call (same colour and 👥 icon).
      { value: "phone_call", label: "Phone Call" },
      { value: "conference_call", label: "Conference Call" },
      { value: "zoom_call", label: "Zoom/Video Call" },
      { value: "opposing_counsel", label: "Opposing Counsel Meeting" },
      { value: "co_counsel_meeting", label: "Co-Counsel Meeting" },
      { value: "expert_meeting", label: "Expert Witness Meeting" },
      { value: "team_meeting", label: "Team Meeting" },
      { value: "staff_meeting", label: "Staff Meeting" },
      { value: "lunch", label: "Lunch" },
      { value: "client_lunch", label: "Client Lunch" },
    ],
  },
  {
    label: "Firm", icon: "🏢",
    options: [
      { value: "firm_administration", label: "Firm Administration" },
      { value: "professional_development", label: "Professional Development" },
      { value: "cle", label: "CLE / MCLE" },
      { value: "business_development", label: "Business Development" },
      { value: "marketing", label: "Marketing" },
      { value: "note", label: "Note" },
      { value: "billing", label: "Billing" },
    ],
  },
  {
    label: "Personal", icon: "👤",
    options: [
      { value: "personal", label: "Personal" },
      { value: "vacation", label: "Vacation" },
      { value: "sick_day", label: "Sick Day" },
      { value: "holiday", label: "Holiday" },
      { value: "red_letter_day", label: "Red Letter Day" },
      { value: "appointment", label: "Appointment" },
    ],
  },
  {
    label: "Other", icon: "📅",
    options: [
      { value: "other", label: "Other" },
    ],
  },
]

const byLabel = (a, b) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' })

export const EVENT_TYPE_GROUPS = RAW_EVENT_TYPE_GROUPS
  .map(g => ({ ...g, options: [...g.options].sort(byLabel) }))
  .sort(byLabel)

export const EVENT_TYPE_OPTIONS_FLAT = EVENT_TYPE_GROUPS.flatMap(g => g.options)

const LABEL_BY_VALUE = new Map(EVENT_TYPE_OPTIONS_FLAT.map(o => [o.value, o.label]))

/**
 * Human label for a stored event_type.  Unknown values (e.g. a type an
 * import created that no picker offers) fall back to a humanized snake_case
 * rather than the raw slug, so a chip never reads "produce_records_documents".
 */
export function eventTypeLabel (value) {
  if (!value) return ''
  const known = LABEL_BY_VALUE.get(value)
  if (known) return known
  return String(value).replace(/_+/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

/**
 * Every option in ONE A–Z list (feedback #826/#827).  Hestia asked twice for
 * "alphabetical"; the first cut sorted within category groups, which still
 * scatters the list and breaks a native <select>'s type-ahead (typing "O"
 * jumps to the first O in whichever group comes first, not the A–Z first).
 * A flat list makes type-to-find behave as people expect.  The two labels that
 * exist in two groups get the group in parentheses so the pair stays
 * distinguishable once they sit side by side.  Groups are still exported —
 * colors and reporting use them.
 */
// Two labels count as "the same" when they differ only in case, punctuation,
// curly-vs-straight quotes or spacing — e.g. "Minor's" vs "Minor’s", which
// sat in different groups before and would otherwise land side by side,
// looking identical, in the flat list.
export const labelKey = (label) => String(label)
  .toLowerCase()
  .replace(/[\u2018\u2019\u02bc]/g, "'")
  .replace(/[^a-z0-9]+/g, '')

export const EVENT_TYPE_OPTIONS_AZ = (() => {
  const groupOf = new Map()
  const seen = new Map()
  for (const g of EVENT_TYPE_GROUPS) {
    for (const o of g.options) {
      groupOf.set(o.value, g.label)
      const k = labelKey(o.label)
      seen.set(k, (seen.get(k) || 0) + 1)
    }
  }
  return EVENT_TYPE_OPTIONS_FLAT
    .map(o => (seen.get(labelKey(o.label)) > 1 ? { ...o, label: `${o.label} (${groupOf.get(o.value)})` } : o))
    .sort(byLabel)
})()

/** The <option>s for an event-type <select>, in one A–Z list. */
export function renderEventTypeOptions () {
  return EVENT_TYPE_OPTIONS_AZ.map(o => <option key={o.value} value={o.value}>{o.label}</option>)
}

/**
 * Tailwind chip colors for an event_type.  Moved here from Calendar.js so the
 * Matter page's events list colors types the same way the calendar does.
 * Every event_type present in production classifies to a non-default color
 * (asserted in eventTypes.test.js against the production list).
 */
export const eventTypeColor = (type) => {
  // Feedback #827 — a vacated / off-calendar event is not happening; muted.
  if (type === 'off_calendar_vacated')
    return 'bg-gray-200 text-gray-500'
  if (['hearing', 'trial', 'trial_date', 'motion_hearing', 'status_conference',
       'pretrial_conference', 'final_status_conference', 'mandatory', 'default',
       'class_certification_motion', 'law_motion_cutoff', 'motions_in_limine',
       'mil_opposition_due', 'mil_reply_due', 'mil_response_due', 'ene_statements'].includes(type))
    return 'bg-red-100 text-red-700'
  if (['settlement_conference', 'complete_settlement_conference', 'arbitration',
       'mediation', 'mediation_settlement_conference', 'closing',
       'minor_compensation'].includes(type))
    return 'bg-pink-100 text-pink-700'
  if (['deposition', 'deposition_defense', 'deposition_plaintiff', 'deposition_expert',
       'deposition_witness', 'witness_interview', 'medical_exam', 'defense_medical_exam',
       'meet_confer', 'inspection_site', 'records_subpoena'].includes(type))
    return 'bg-purple-100 text-purple-700'
  if (type?.startsWith('f_') || ['filing_deadline', 'notice', 'proof_of_service',
      'petition_juvenile_court', 'tort_claim', 'minor_s_compensation', 'transcript'].includes(type))
    return 'bg-orange-100 text-orange-700'
  if (['brief_due', 'opening_brief_due', 'answering_brief_due', 'reply_brief_due',
       'answering_complaint_due', 'amended_pleadings', 'response_to_pleading', 'opposition_due', 'reply_due',
       'mediation_brief', 'mediation_questionnaire', 'declaration_due',
       'cmc_statement_due', 'joint_report_due', 'trial_brief_due',
       'oppo_smj', 'reply_smj', 'opening_briefs_due', 'responding_briefs_due',
       'settlement_briefs_due', 'respond_due', 'stipulation', 'statement_due',
       'report_due', 'status_reports', 'trc_report_due', 'witness_lists_due',
       'mediation_questionnaire_due', 'supplemental_due', 'objection_due',
       'jury_instructions_due', 'verdict_form_due', 'witness_list_due',
       'exhibit_list_due', 'pretrial_statement_due',
       'trial_prep', 'trial_filings'].includes(type))
    return 'bg-amber-100 text-amber-700'
  if (['discovery_deadline', 'discovery_responses_due', 'rogs_responses_due',
       'produce_records_documents', 'respond_to_meet_and_confer', 'discovery_cut_off',
       'discovery_response_due', 'expert_discovery_cut_off', 'written_discovery_cut_off',
       'complete_general_fact_discovery', 'rebuttal_experts', 'medical_exam_ime',
       'subpoena', 'demand',
       'rpd_responses_due', 'rfa_responses_due', 'disclosure_due', 'initial_disclosures',
       'supplemental_disclosures', 'discovery_cutoff', 'complete_fact_discovery',
       'non_expert_discovery_cutoff', 'expert_designation', 'rebuttal_expert_designation',
       'expert_discovery_cutoff', 'expert_report_due'].includes(type))
    return 'bg-yellow-100 text-yellow-700'
  if (['response_due', 'appeal_deadline', 'notice_of_appeal_due',
       'statute_of_limitations', 'sol_tolling_expires', 'demand_response_due',
       'deadline', 'reminder'].includes(type))
    return 'bg-orange-200 text-orange-800'
  // phone_call joined the Meetings group (feedback #846) — blue, not green.
  if (['client_meeting', 'initial_consultation', 'meeting', 'phone_call', 'conference_call',
       'zoom_call', 'opposing_counsel', 'co_counsel_meeting', 'expert_meeting',
       'team_meeting', 'staff_meeting', 'lunch', 'client_lunch'].includes(type))
    return 'bg-blue-100 text-blue-700'
  if (['client_work_general', 'client_work_special', 'follow_up', 'draft',
       'document_review', 'inspection', 'minor_compensation', 'court'].includes(type))
    return 'bg-teal-100 text-teal-700'
  if (['firm_administration', 'professional_development', 'cle',
       'business_development', 'marketing', 'note', 'billing'].includes(type))
    return 'bg-indigo-100 text-indigo-700'
  if (['personal', 'vacation', 'sick_day', 'holiday', 'red_letter_day',
       'appointment'].includes(type))
    return 'bg-green-100 text-green-700'
  return 'bg-gray-100 text-gray-700'
}

/**
 * Emoji icon for an event_type, shown in front of the title on the Calendar
 * (month / week / day / list / detail popup), the To-Do list and Dailies.
 *
 * Code review of 9ff73f1, finding #9 — this used to be copied into
 * Calendar.js, Tasks.js and Dailies.js.  The Tasks copy lacked the 🏢 firm
 * branch, and the Dailies copy had drifted much further (no
 * final_status_conference, default_hearing, mediation_settlement_conference,
 * law_motion_cutoff, the mil_* types, most deposition and meeting types…),
 * so the same event wore a different icon depending on the page.  The
 * Calendar copy was a strict superset of both others, so it is the one kept
 * here, unchanged.  eventTypes.test.js asserts no page defines its own copy
 * again.
 *
 * Order matters: the ⚖️ list is checked first, so a court deadline such as
 * mil_opposition_due or law_motion_cutoff is ⚖️ even though it also matches
 * the ⏰ substrings ('due', 'cutoff') further down.
 */
export const getEventIcon = (eventType) => {
  if (['hearing', 'trial', 'trial_date', 'motion_hearing', 'status_conference',
       'pretrial_conference', 'final_status_conference', 'settlement_conference',
       'complete_settlement_conference', 'arbitration', 'mediation',
       'mediation_settlement_conference', 'mandatory', 'default', 'default_hearing',
       'class_certification_motion', 'law_motion_cutoff', 'motions_in_limine',
       'mil_opposition_due', 'mil_reply_due', 'mil_response_due', 'ene_statements',
       'closing', 'judgment_due', 'order_due', 'writ_due'].includes(eventType))
    return '⚖️'
  if (['deposition', 'deposition_defense', 'deposition_plaintiff', 'deposition_expert',
       'deposition_witness', 'witness_interview', 'medical_exam', 'defense_medical_exam',
       'meet_confer', 'inspection_site', 'records_subpoena'].includes(eventType))
    return '📋'
  if (eventType?.includes('deadline') || eventType?.includes('due') ||
      eventType?.includes('cutoff') || eventType?.includes('designation') ||
      eventType?.includes('disclosures') || eventType?.includes('discovery') ||
      eventType?.startsWith('f_') || eventType?.includes('brief') ||
      eventType?.includes('statute') || eventType?.includes('appeal') ||
      eventType?.includes('response') || eventType === 'mandatory' ||
      eventType === 'reminder' || eventType?.includes('amended') ||
      // Amicus "Trial Prep" / "Trial Filings" deadlines (re-typed out of
      // 'trial'); neither name contains any of the substrings above.
      eventType === 'trial_prep' || eventType === 'trial_filings')
    return '⏰'
  // phone_call joined the Meetings group (feedback #846) — 👥, not 📅.
  if (['client_meeting', 'initial_consultation', 'meeting', 'phone_call', 'conference_call',
       'zoom_call', 'opposing_counsel', 'co_counsel_meeting', 'expert_meeting',
       'team_meeting', 'staff_meeting', 'lunch', 'client_lunch'].includes(eventType))
    return '👥'
  if (['appointment', 'personal', 'vacation', 'sick_day',
       'holiday', 'red_letter_day'].includes(eventType))
    return '📅'
  if (['firm_administration', 'professional_development', 'cle',
       'business_development', 'marketing', 'note', 'billing'].includes(eventType))
    return '🏢'
  return '📌'
}

